export const STORAGE_KEY = 'resetDepotAssets:inFlightRun';
export const TTL_MS = 24 * 60 * 60 * 1000;

type StoredRun = {
  id: string;
  accountId: string;
  startedAt: number;
};

export const clearInFlightRun = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const saveInFlightRun = (id: string, accountId: string): void => {
  const run: StoredRun = { id, accountId, startedAt: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(run));
};

/**
 * `JSON.parse` can return non-object values without throwing, so the shape is
 * validated before any field is touched. `startedAt` must be a number too: a
 * missing one makes `Date.now() - undefined` evaluate to `NaN`, which never
 * exceeds the TTL, so the entry would never expire.
 */
const isStoredRun = (value: unknown): value is StoredRun =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredRun).id === 'string' &&
  typeof (value as StoredRun).accountId === 'string' &&
  typeof (value as StoredRun).startedAt === 'number';

/**
 * There is no `depotResets` index query, only `depotReset(id:)` — losing this
 * id makes the run unrecoverable, which is why a reload can resume it. The
 * accountId guard stops a reload on a different account from reopening a
 * wizard for depots not on screen; the TTL is only a crash backstop.
 */
export const readInFlightRun = (accountId: string): string | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearInFlightRun();
    return null;
  }

  if (!isStoredRun(parsed)) {
    clearInFlightRun();
    return null;
  }

  const run = parsed;

  if (run.accountId !== accountId) return null;

  // `id: ''` passes the `typeof === 'string'` shape check above, so it still needs
  // an explicit reject here.
  if (!run.id || Date.now() - run.startedAt > TTL_MS) {
    clearInFlightRun();
    return null;
  }

  return run.id;
};
